import { inject, provide } from 'vue';
import type { ComputedRef, InjectionKey, Ref } from 'vue';
import type { ICatalogAccess } from '@/domain/catalog/catalog-access';
import type { ICatalogPageState } from './catalog-page-state';
import type { ICatalogActions } from './use-catalog-actions';
import type { ICatalogCollapse } from './use-catalog-collapse';
import type { ICatalogCommands } from './use-catalog-commands';
import type { ICatalogData } from './use-catalog-data';
import type { ICatalogLayout } from './use-catalog-layout';
import type { ICatalogSchedule } from './use-catalog-schedule';
import type { ICatalogUrl } from './use-catalog-url';
import type { ICatalogWindowing } from './use-catalog-windowing';

/**
 * Everything the Menú page's components share (plan §5.5), built once by
 * `useCatalogPageSetup` in the route component and injected below it
 * (the `menu-import-context.ts` pattern).
 */
export interface ICatalogPageContext {
  state: ICatalogPageState;
  layout: ICatalogLayout;
  data: ICatalogData;
  access: ComputedRef<ICatalogAccess>;
  /** Writes are disabled while offline (nothing is queued). */
  online: Ref<boolean>;
  url: ICatalogUrl;
  collapse: ICatalogCollapse;
  windowing: ICatalogWindowing;
  /** «Fuera de horario» statuses, recomputed every minute. */
  schedule: ICatalogSchedule;
  actions: ICatalogActions;
  /** What the ⋯ menus' entries do, and the page modes. */
  commands: ICatalogCommands;
  /** «¿Descartar los cambios…?» when the editor is dirty; true to go on. */
  confirmDiscard(): Promise<boolean>;
  /** «¿Descartar el nuevo orden?» when reorder mode has unsaved changes. */
  confirmReorderDiscard(): Promise<boolean>;
  /** Scrolls a category card under the sticky head (mounting it first). */
  jumpToCategory(categoryId: number): Promise<void>;
}

export const CATALOG_PAGE_KEY: InjectionKey<ICatalogPageContext> = Symbol(
  'catalog-page'
);

export function provideCatalogPage(context: ICatalogPageContext): void {
  provide(CATALOG_PAGE_KEY, context);
}

/** Throws outside the Menú page. */
export function useCatalogPage(): ICatalogPageContext {
  const context = inject(CATALOG_PAGE_KEY, null);
  if (!context)
    throw new Error('useCatalogPage() must be called inside the Menú page');
  return context;
}
